import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Divider,
} from "@mui/material";
import { Tablecell } from "../../../../component/Methods";

const SummarySectionThree = ({ currentSignalsData, modifiedSignalsData }) => {
  const [CombinedArray, setCombinedArray] = useState([]);

  useEffect(() => {
    // Only show data when we have modified signals data
    if (modifiedSignalsData && modifiedSignalsData.length > 0 && currentSignalsData) {
      // Helper function to normalize ticker name (use full name to preserve uniqueness)
      const normalizeTicker = (name) => {
        if (!name) return "";
        // Use the full name (including /ASSET part) to preserve uniqueness
        // This ensures "BKR/A" and "BKR/B" are treated as different tickers
        return name.trim().toUpperCase();
      };

      // Helper function to normalize long_short value
      const normalizeLongShort = (value, existingLongShort) => {
        // Always derive from value to ensure accuracy (value is the source of truth)
        // If value is negative, it's short; if positive or zero, it's long
        const numValue = parseFloat(value) || 0;
        return numValue >= 0 ? "L" : "S";
      };

      // Create maps for easier lookup
      const currentMap = new Map();
      const modifiedMap = new Map();

      // Build current signals map
      currentSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;
        
        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);
        
        currentMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Build modified signals map
      modifiedSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;
        
        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);
        
        modifiedMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Find all unique tickers from both sets
      const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

      // Calculate metrics
      let shortWeightChange = 0;
      let longWeightChange = 0;
      let openPositions = 0;
      let closedPositions = 0;

      allTickers.forEach(ticker => {
        const currentSignal = currentMap.get(ticker);
        const modifiedSignal = modifiedMap.get(ticker);

        if (currentSignal && modifiedSignal) {
          // Ticker exists in both - calculate weight change
          const currentValue = parseFloat(currentSignal.value) || 0;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;
          
          // Calculate long and short weight changes separately
          const isCurrentLong = currentValue >= 0;
          const isModifiedLong = modifiedValue >= 0;
          
          if (isCurrentLong && isModifiedLong) {
            // Both long: long weight change = modified - current
            // Example: 10% -> 20% = +10%, 10% -> 5% = -5%
            longWeightChange += (modifiedValue - currentValue);
            // No short change
          } else if (!isCurrentLong && !isModifiedLong) {
            // Both short: short weight change = modified - current
            // Example: -4% -> -2% = +2% (reducing short), -4% -> -8% = -4% (increasing short)
            shortWeightChange += (modifiedValue - currentValue);
            // No long change
          } else if (isCurrentLong && !isModifiedLong) {
            // Long to short: close long, open short
            // Example: 10% -> -10%: long change = 0 - 10 = -10, short change = -10 - 0 = -10
            longWeightChange += (0 - currentValue); // Closing long position
            shortWeightChange += (modifiedValue - 0); // Opening short position
          } else if (!isCurrentLong && isModifiedLong) {
            // Short to long: close short, open long
            // Example: -10% -> 10%: short change = 0 - (-10) = +10, long change = 10 - 0 = +10
            longWeightChange += (modifiedValue - 0); // Opening long position
            shortWeightChange += (0 - currentValue); // Closing short position (currentValue is negative, so this becomes positive)
          }
        } else if (!currentSignal && modifiedSignal) {
          // Only in modified signals - OPEN position
          openPositions++;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;
          
          if (modifiedValue >= 0) {
            // Opening long position
            longWeightChange += modifiedValue;
          } else {
            // Opening short position
            shortWeightChange += modifiedValue;
          }
        } else if (currentSignal && !modifiedSignal) {
          // Only in current signals - CLOSED position
          closedPositions++;
          const currentValue = parseFloat(currentSignal.value) || 0;
          
          if (currentValue >= 0) {
            // Closing long position
            longWeightChange += (0 - currentValue);
          } else {
            // Closing short position
            shortWeightChange += (0 - currentValue); // currentValue is negative, so this becomes positive
          }
        }
      });

          function roundit(number) {
            return number.toFixed(2).replace(/\.?0+$/, "");
          }

          const transformedArray = [
        { name: "Short Weight Change", value: roundit(shortWeightChange) },
        { name: "Long Weight Change", value: roundit(longWeightChange) },
        { name: "Open Positions", value: openPositions },
        { name: "Closed Positions", value: closedPositions },
          ];

          setCombinedArray(transformedArray);
        } else {
          setCombinedArray([]);
        }
  }, [currentSignalsData, modifiedSignalsData]);

  const theme = useTheme();
  
  // Don't render anything if there's no modified signals data
  if (!modifiedSignalsData || modifiedSignalsData.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          border: `100px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "10px",
          width: "80%",
          marginRight: 1,
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: `1px solid ${theme.palette.divider}`,
            marginTop: "0px",
          }}
        >
          <TableBody>
            {CombinedArray?.map((row, index) => (
              <TableRow
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },

                  backgroundColor:
                    row.name === "Total Performance"
                      ? row.value > 0
                        ? theme.palette.green.longPositions
                        : theme.palette.red.shortPositions
                      : "transparent",
                }}
              >
                <Tablecell
                  align="left"
                  sx={{
                    fontWeight: "900",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom:
                      row.name === "Long Weight Change"
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                  }}
                >
                  {row?.name}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom:
                      row.name === "Long Weight Change"
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                  }}
                >
                  {row.name === "Open Positions" || row.name === "Closed Positions"
                    ? row.value
                    : `${row.value}%`}
                </Tablecell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
export default SummarySectionThree;
